import { images } from "./images";

export const insights = [
  {
    slug: "security-guard-services-thunder-bay-guide",
    title: "Security Guard Services in Thunder Bay: A Northern Ontario Buyer's Guide",
    excerpt: "Port facilities, rail yards, healthcare and a sprawling geography — what Thunder Bay organizations should look for in a security partner.",
    date: "2026-07-30",
    image: images.patrol,
    sections: [
      {
        h: "The northern risk picture",
        p: "Thunder Bay's economy runs through its port, rail yards, hospital and industrial sites — large, open properties where a single overnight incident can halt operations. Distances are long, winters are hard, and police response to remote industrial areas takes time. Physical security fills that gap.",
      },
      {
        h: "Licensing is the same everywhere in Ontario",
        p: "Whether your site is on the Toronto waterfront or the Thunder Bay harbourfront, every guard must hold a valid PSISA licence. Ask for licence numbers and insurance certificates before signing — northern sites are not exempt, and neither are the guards working them.",
      },
      {
        h: "What good coverage looks like up north",
        p: "Expect written post orders, GPS-verified patrols that stand up to scrutiny, supervisors who actually visit sites, and a dispatch line that answers at 3 a.m. in February. A provider that can't describe its supervision model for remote posts hasn't run them.",
      },
    ],
  },
  {
    slug: "first-time-hiring-security-small-business",
    title: "Hiring Security for the First Time: A Small Business Owner's Checklist",
    excerpt: "Never contracted security before? The five decisions that determine whether your first guard program works.",
    date: "2026-07-28",
    image: images.commercial,
    sections: [
      {
        h: "Decide what you're actually protecting against",
        p: "Theft, trespass, aggressive incidents, after-hours access — write down the incidents that pushed you to hire. That list determines whether you need a static guard, mobile patrols, or just better alarm response. A good provider will challenge your assumptions here, not just agree.",
      },
      {
        h: "Verify before you sign",
        p: "Three documents separate professionals from pretenders: the agency's provincial security business licence, individual guard licences, and a certificate of commercial liability insurance naming your business. All three should arrive without you having to ask twice.",
      },
      {
        h: "Start measured, scale on evidence",
        p: "Many first-time buyers start with randomized mobile patrols — visible deterrence at a fraction of full-time cost — then add static coverage where the incident reports justify it. Demand time-stamped reporting from day one so you're scaling on evidence, not anxiety.",
      },
    ],
  },
  {
    slug: "quebec-festival-season-event-security",
    title: "Festival Season in Quebec: Event Security Planning for Summer Organizers",
    excerpt: "From Montreal's festival district to regional fairs — licensing, bilingual teams and crowd planning for Quebec events.",
    date: "2026-07-25",
    image: images.guard,
    sections: [
      {
        h: "BSP permits are non-negotiable",
        p: "Every security guard working your Quebec event must hold a valid agent permit from the Bureau de la sécurité privée — including temporary and out-of-province staff. Venues and insurers increasingly verify, so build licence checks into your vendor onboarding, not your post-incident review.",
      },
      {
        h: "Bilingual teams de-escalate better",
        p: "Crowd interactions go sideways when communication fails. Front-line event staff in Quebec should be functionally bilingual, trained in verbal de-escalation, and briefed on your venue's specific layout and emergency codes before gates open.",
      },
      {
        h: "Plan the crowd, not just the guards",
        p: "Entry screening lanes, queue design, capacity monitoring and egress routes matter more than raw headcount. A professional security partner walks the site, maps pinch points and writes the plan — if your quote arrives without a site visit, keep looking.",
      },
    ],
  },
  {
    slug: "campus-security-checklist-universities",
    title: "Campus Security: A Semester-Ready Checklist for Canadian Colleges and Universities",
    excerpt: "September brings hundred-thousand-student surges back to Canadian campuses. The security groundwork to lay before orientation week.",
    date: "2026-07-23",
    image: images.commercial,
    sections: [
      {
        h: "The September surge is predictable — prepare for it",
        p: "Orientation week concentrates crowds, alcohol, emotions and strangers into a few days. Staffing plans, event coverage, residence access control and parking patrols should be contracted and briefed by mid-August, not assembled during move-in weekend.",
      },
      {
        h: "Vetting matters more on campus",
        p: "Campus guards work around minors, vulnerable students and residences. Beyond provincial licensing, require enhanced background checks, safeguarding awareness training and a clear conduct standard. Approachability is a security feature: students report problems to guards they trust.",
      },
      {
        h: "Cover the quiet hours",
        p: "Libraries at midnight, parkades after evening classes, residences on long weekends — incident patterns cluster in low-traffic windows. Verified overnight patrols with documented checkpoints protect students and give administrators defensible records when something does happen.",
      },
    ],
  },
  {
    slug: "hotel-security-tourist-season-canada",
    title: "Tourist Season Hotel Security: Preparing Niagara Falls, Banff and Old Quebec Properties",
    excerpt: "Peak season triples occupancy, events and incidents. How destination hotels scale security without hurting the guest experience.",
    date: "2026-07-21",
    image: images.guard,
    sections: [
      {
        h: "Peak season multiplies everything",
        p: "Full houses, weddings, conferences and tour groups arrive together. So do the incidents: unauthorized pool access, room-party noise, parking lot break-ins and altercations at last call. Summer staffing plans should be set in spring — seasonal guard contracts take time to fill with licensed people.",
      },
      {
        h: "Hospitality-first, enforcement-ready",
        p: "Hotel security is a guest-relations role until the second it isn't. The best officers give directions and hold doors all evening, then handle a removal with calm, lawful precision at midnight. Train for both, and brief every guard on your property's exact escalation policy.",
      },
      {
        h: "Document for the claim you hope never comes",
        p: "Slip-and-falls, alleged thefts, ejections — destination properties face claims months later. Time-stamped incident reports, preserved video and witness details turn a vague accusation into a settled file. Make reporting quality a contract requirement, not a hope.",
      },
    ],
  },
  {
    slug: "mobile-patrol-metro-vancouver-multi-site",
    title: "Multi-Site Security in Metro Vancouver: How Mobile Patrol Protects Portfolios",
    excerpt: "Strata councils, retail plazas and construction portfolios across the Lower Mainland share one problem: too many sites, one budget.",
    date: "2026-07-18",
    image: images.patrol,
    sections: [
      {
        h: "The portfolio math",
        p: "A property manager with twelve sites can't put a full-time guard on each — and most don't need one. Randomized marked-vehicle patrols deliver visible deterrence across every property nightly, at a cost per site that strata councils and ownership groups actually approve.",
      },
      {
        h: "Verification is the product",
        p: "Any company can claim nightly patrols; few prove them. GPS-stamped checkpoint scans, photographic condition reports and time-stamped incident logs let you audit coverage per site, per night. In a region where property values demand documentation, that's the difference between a vendor and a partner.",
      },
      {
        h: "Combine patrol with response",
        p: "The strongest Metro Vancouver programs pair scheduled patrols with alarm response and keyholding: the same unit that checks your properties attends when an alarm trips. One contract, one keyholder protocol, one reporting stream across the portfolio.",
      },
    ],
  },
  {
    slug: "gta-logistics-corridor-warehouse-security",
    title: "Securing the 401 Corridor: Warehouse and Logistics Security in the GTA",
    excerpt: "The busiest freight corridor in Canada is also a top cargo-theft target. Layered controls for warehouses from Mississauga to Pickering.",
    date: "2026-07-16",
    image: images.construction,
    sections: [
      {
        h: "Why the corridor is targeted",
        p: "The 401/400 corridor concentrates more freight value per square kilometre than anywhere in Canada — and organized theft crews know the lanes, the yards and the shift changes. Fictitious pickups and trailer theft spike around long weekends and peak shipping seasons.",
      },
      {
        h: "The gate is your strongest control",
        p: "Every driver, tractor and trailer entering should be verified against a manifest, photographed and logged; every outbound seal checked and recorded. Gatehouse discipline stops most organized attempts before they reach your dock — crews move on to yards with waving-through gates.",
      },
      {
        h: "Layer the interior",
        p: "High-value cage coverage on camera, exception reporting at the dock, uniformed presence during shipping windows and overnight patrols create overlapping layers. Design the system so no single point of failure — human or technical — lets product leave the building undocumented.",
      },
    ],
  },
  {
    slug: "winter-construction-site-security-canada",
    title: "Construction Site Security Through the Canadian Winter",
    excerpt: "Short days, idle sites and frozen perimeters: winter is when Canadian job sites are most exposed — and least watched.",
    date: "2026-07-14",
    image: images.construction,
    sections: [
      {
        h: "Winter changes the risk, not the need",
        p: "Sites slow down or shut down, crews thin out, and darkness arrives at 4:30 p.m. — while copper, tools, fuel and equipment stay exactly where they were. Insurers report theft and vandalism claims climbing through the cold months precisely because sites feel 'closed.'",
      },
      {
        h: "Cold-weather coverage that holds up",
        p: "Winter guarding demands heated shelter for static posts, vehicle patrols equipped for ice and distance, and checkpoint schedules that account for dangerous conditions. Ask any provider how their winter post orders differ from summer — a blank look is your answer.",
      },
      {
        h: "Fire watch matters more in winter",
        p: "Temporary heaters, idling equipment and impaired hydrants raise fire risk on dormant sites. Where suppression systems are winterized or offline, documented fire watch patrols keep you compliant with your insurer and the fire code — and keep a smoulder from becoming a total loss.",
      },
    ],
  },
  {
    slug: "fire-watch-logs-insurance-fire-marshal",
    title: "Fire Watch Logs: What Fire Marshals and Insurers Actually Look For",
    excerpt: "A fire watch without proper logs is a fire watch you can't prove. The documentation standards that protect your building and your coverage.",
    date: "2026-07-11",
    image: images.patrol,
    sections: [
      {
        h: "What authorities expect to see",
        p: "When the fire department orders or accepts a fire watch, they expect continuous patrols of affected areas, a written log with times and initials for every round, immediate notification capability, and a record of any hazards found. 'We had someone walking around' is not a fire watch.",
      },
      {
        h: "The log is the evidence",
        p: "Professional fire watch logs capture patrol times, areas checked, observations and actions — time-stamped and tamper-evident. After an incident, that document answers the only questions insurers and investigators ask: was the watch real, continuous and documented?",
      },
      {
        h: "Common failures to avoid",
        p: "Gappy patrol intervals, untrained staff doubling as watchers, missing signatures and logs reconstructed after the fact. Each one gives an insurer a reason to deny and a plaintiff a reason to sue. A professional fire watch service exists to make those failures impossible.",
      },
    ],
  },
  {
    slug: "strike-labour-dispute-security-canada",
    title: "Strike and Labour Dispute Security: Lawful, Calm Coverage for Canadian Workplaces",
    excerpt: "Labour disruptions are legal flashpoints. How professional security protects people, property and evidence — without escalating anything.",
    date: "2026-07-09",
    image: images.guard,
    sections: [
      {
        h: "Restraint is the entire job",
        p: "Picket lines are emotionally charged and legally protected. Security's role is not to confront — it's to keep access points safe, document what happens and protect people crossing either direction. Guards assigned to labour disputes need de-escalation training and the discipline to never become the story.",
      },
      {
        h: "Evidence wins disputes later",
        p: "Injunctions, grievances and court proceedings turn on documentation. Date-stamped video, objective incident logs and witness details — gathered lawfully from your property — give counsel what they need. Reconstructed recollections from the picket line give them nothing.",
      },
      {
        h: "Prepare before the vote, not after",
        p: "The time to plan strike security is during bargaining, not at the picket line: site maps, gate protocols, escort procedures, contractor communications and police liaison arrangements. Organizations that plan early experience calmer strikes and shorter ones.",
      },
    ],
  },
  {
    slug: "condo-package-theft-concierge-security",
    title: "Condo Package Theft: How Concierge Security Stops Porch Piracy at the Door",
    excerpt: "Parcel theft is the complaint every property manager hears weekly. The building-level controls that end it.",
    date: "2026-07-08",
    image: images.commercial,
    sections: [
      {
        h: "The numbers behind the complaints",
        p: "E-commerce growth turned every condo lobby into a parcel depot. Unattended lobbies and propped doors let thieves walk out with armfuls — and every incident lands on the property manager's desk as a complaint, a review, or a board motion.",
      },
      {
        h: "Access control is the first wall",
        p: "Tailgating defeats most buildings: a courier buzzes, a stranger follows. Trained concierge staff who verify every visitor, working door hardware, and a no-prop policy close the gap that cameras only record. Package rooms with logged, credential-based access remove the temptation entirely.",
      },
      {
        h: "Concierge as the system of record",
        p: "A staffed front desk logs deliveries, holds parcels under signature, and produces the audit trail when a claim arises. Buildings with professional concierge programs report fewer thefts and faster resolutions — and residents notice the difference in the first month.",
      },
    ],
  },
  {
    slug: "security-guard-licence-requirements-canada",
    title: "Security Guard Licence Requirements in Canada: Province-by-Province Guide",
    excerpt: "Every province licenses security guards differently. A plain-language guide to training, testing and permits across Canada.",
    date: "2026-07-08",
    image: images.guard,
    sections: [
      {
        h: "Why licensing matters to buyers, not just guards",
        p: "When you hire a security company, its guards' licences become your liability shield. An unlicensed guard on your property can complicate insurance claims, expose you to provincial fines and undermine any legal defence after an incident. Verifying licensing takes minutes and should be step one of any contract.",
      },
      {
        h: "The provincial patchwork",
        p: "Ontario requires a 40-hour course and ministry exam under the PSISA. Quebec's Bureau de la sécurité privée mandates a 70-hour program before issuing an agent permit. British Columbia requires Basic Security Training under the Security Services Act, and Alberta licenses guards under the SSIA. The other provinces and territories run their own registries with similar training, background-check and renewal requirements.",
      },
      {
        h: "What to ask any security provider",
        p: "Ask for the company's agency licence number, the individual licences of guards assigned to your site, and proof of commercial liability insurance. Professional companies answer these questions in writing before the first shift — hesitation here is the clearest red flag in the industry.",
      },
    ],
  },
  {
    slug: "what-drives-security-guard-costs-canada",
    title: "What Drives the Cost of Security Guard Services in Canada",
    excerpt: "Coverage hours, risk level, training requirements and supervision — the factors that actually shape a security quote.",
    date: "2026-07-02",
    image: images.commercial,
    sections: [
      {
        h: "Coverage model is the biggest lever",
        p: "A full-time static post costs more than randomized mobile patrols because you are buying dedicated hours. Many properties blend the two: a static guard during high-risk periods and vehicle patrols overnight. The right mix comes from an honest look at when incidents actually happen on your site.",
      },
      {
        h: "Risk and skill requirements",
        p: "A lobby concierge post and an emergency-department watch are both 'a guard', but they demand very different training, temperament and supervision. De-escalation-certified healthcare guards, bilingual front-of-house staff and tactical response teams each sit at different points on the cost curve.",
      },
      {
        h: "What a quote should always include",
        p: "Whatever the number, a professional quote should name what's behind it: licensed guards, field supervision, verified patrol technology, digital reporting and insurance. A cheap quote that omits supervision and reporting isn't cheaper — it just moves the cost into incidents you'll discover later.",
      },
    ],
  },
  {
    slug: "cargo-theft-prevention-canada",
    title: "Cargo Theft in Canada: How Carriers and Warehouses Fight Back",
    excerpt: "Organized crews target freight at yards, docks and rest stops. The layered controls that keep loads moving and intact.",
    date: "2026-06-24",
    image: images.patrol,
    sections: [
      {
        h: "A growing, organized problem",
        p: "Insurers and industry associations across Canada report cargo theft climbing year over year, much of it organized: fictitious pickups, trailer theft from unsecured yards, and targeted hits on high-value loads. Recovery rates remain low, which makes prevention the only strategy that pays.",
      },
      {
        h: "Harden the yard first",
        p: "Most cargo losses start on the ground: perimeter fencing, lighting, gatehouse verification of every driver and trailer, seal checks on inbound and outbound loads, and GPS-verified guard patrols after hours. A yard that controls its gate controls its risk.",
      },
      {
        h: "Protect the load in transit",
        p: "For high-value freight, escorted transport, documented chain-of-custody handoffs and driver check-in protocols close the gap between dock doors. Every handoff should produce a record — because if a load disappears, that documentation is what your insurer and the police will ask for first.",
      },
    ],
  },
  {
    slug: "condominium-security-guide-property-managers",
    title: "Condominium Security: A Property Manager's Guide to Concierge and Patrol Coverage",
    excerpt: "From package rooms to parkades — how condo boards and managers should structure building security that residents actually appreciate.",
    date: "2026-06-17",
    image: images.commercial,
    sections: [
      {
        h: "Start with the complaint log",
        p: "The best condominium security programs are designed backwards from real problems: unauthorized entry, package theft, parkade break-ins, noise complaints and amenity misuse. Your incident history — not a generic template — should decide where coverage hours go.",
      },
      {
        h: "Concierge versus patrol",
        p: "A 24/7 concierge controls the front door, screens visitors and manages keys and deliveries. Courtesy patrols cover what the desk can't see: parkades, stairwells, amenity floors and the building envelope. Buildings with both see measurably fewer unauthorized entries and faster incident response.",
      },
      {
        h: "Documentation is the deliverable",
        p: "Boards and owners should expect daily logs, incident reports with timestamps, and visitor records that stand up to scrutiny. When a dispute or insurance claim lands, the building with professional documentation settles it in days — the building without it settles it in months.",
      },
    ],
  },
  {
    slug: "workplace-violence-healthcare-security",
    title: "Workplace Violence in Healthcare: The Case for Trained Security Guards",
    excerpt: "Canadian healthcare workers face some of the highest rates of workplace violence of any profession. What real protection looks like.",
    date: "2026-06-10",
    image: images.guard,
    sections: [
      {
        h: "The scale of the problem",
        p: "Nurses, paramedics and emergency staff report verbal and physical aggression at rates far above most industries. Overcrowded emergency departments, long waits and patients in crisis create conditions where incidents escalate in seconds — and staff shouldn't face them alone.",
      },
      {
        h: "De-escalation is the core skill",
        p: "Healthcare security is a specialty, not a generic posting. Guards need formal training in verbal de-escalation, trauma-informed communication and safe physical intervention as a last resort. The goal is always the same: protect patients and staff while preserving dignity and care.",
      },
      {
        h: "Beyond the emergency department",
        p: "Effective programs cover patient watch for at-risk individuals, controlled access to maternity and pharmacy areas, parking escorts for night-shift staff, and detailed incident documentation that health authorities can act on. Security becomes part of the care environment, not apart from it.",
      },
    ],
  },
  {
    slug: "event-security-insurance-requirements",
    title: "Event Security and Insurance: What Venues and Organizers Need in Place",
    excerpt: "Liability for what happens at your event lands on the organizer. The security and insurance groundwork that protects you.",
    date: "2026-06-03",
    image: images.guard,
    sections: [
      {
        h: "Your insurer will ask about security",
        p: "Event liability policies routinely require a documented security plan: guard counts, screening procedures, emergency protocols and licensed personnel. Gaps between your plan and your policy wording are where claims get denied — read both together before show day.",
      },
      {
        h: "Licensed guards are not optional",
        p: "Provincial regulators require event security staff to hold valid security licences, and venues increasingly demand proof. Volunteers and untrained staff can manage queues and wristbands, but anything involving ejections, physical intervention or emergency response belongs to licensed professionals.",
      },
      {
        h: "Write the plan down",
        p: "A professional security partner produces a written plan: site map, post assignments, communications protocol, evacuation procedures and an incident reporting process. If something goes wrong, that document is your evidence that you took reasonable care — the standard every liability claim turns on.",
      },
    ],
  },
  {
    slug: "vacant-property-security-guide",
    title: "Vacant Property Security: Protecting Empty Buildings from Squatters, Theft and Fire",
    excerpt: "Empty buildings attract trouble fast. How owners keep vacant properties insured, intact and off the incident reports.",
    date: "2026-05-27",
    image: images.construction,
    sections: [
      {
        h: "Vacancy changes your risk overnight",
        p: "A building between tenants or awaiting demolition becomes a magnet for copper theft, squatting, vandalism and arson. Most commercial insurance policies also change at vacancy — many require documented inspections or protective measures to keep coverage valid. Check your policy the day the building empties, not after the first incident.",
      },
      {
        h: "Make it look occupied, prove it's watched",
        p: "Boarded access points, maintained lighting, cleared mail and regular verified patrol inspections signal an active owner. GPS-stamped patrol reports do double duty: they deter through visible presence and satisfy insurers that the property is being monitored.",
      },
      {
        h: "Fire is the catastrophic risk",
        p: "Vacant buildings burn at disproportionate rates, often from trespasser activity. Monitored alarms, documented patrols and — when systems are impaired — a professional fire watch keep a vacant property from becoming a total loss and a liability headline.",
      },
    ],
  },
  {
    slug: "organized-retail-crime-canada",
    title: "Organized Retail Crime in Canada: What Store Owners Are Up Against",
    excerpt: "Coordinated theft crews, resale networks and rising aggression — and the countermeasures that actually reduce losses.",
    date: "2026-05-20",
    image: images.surveillance,
    sections: [
      {
        h: "This is not shoplifting as usual",
        p: "Retail associations across Canada report a shift from opportunistic theft to organized operations: crews hitting multiple stores in a day, targeting specific high-value goods, and reselling through online marketplaces. Aggression toward staff during incidents has risen alongside the losses.",
      },
      {
        h: "People first, technology second",
        p: "Trained loss prevention officers remain the most effective single countermeasure: they recognize crew behaviours, coordinate with store teams and intervene within the law. Uniformed presence at entrances changes the risk calculation for crews scouting locations — they simply move to softer targets.",
      },
      {
        h: "Build the case, not just the stop",
        p: "Organized crews return. Plate logs, incident reports with timestamps, preserved video and communication with police and neighbouring retailers turn isolated incidents into patterns that prosecutors can act on. Documentation is what converts store-level defence into region-level deterrence.",
      },
    ],
  },
  {
    slug: "office-building-access-control-best-practices",
    title: "Office Building Access Control: Best Practices for Property Managers",
    excerpt: "Tailgating, lost fobs and after-hours gaps — practical access control habits that keep towers secure without frustrating tenants.",
    date: "2026-05-18",
    image: images.commercial,
    sections: [
      {
        h: "Tailgating is the real perimeter breach",
        p: "Most unauthorized entries don't defeat the card reader — they follow someone through it. Trained lobby staff who greet and screen visitors, combined with anti-passback rules and tenant education, close the gap that hardware alone leaves open.",
      },
      {
        h: "Manage credentials like inventory",
        p: "Every fob should have an owner, an expiry and an audit trail. Deactivate credentials the day tenants or employees depart, audit active cards quarterly, and treat master keys with signed custody logs. Credential hygiene is unglamorous and stops more incidents than any camera.",
      },
      {
        h: "After hours is when it matters",
        p: "Evenings and weekends are when towers are emptiest and incidents costliest. Defined lock-up procedures, verified evening patrols, contractor sign-in rules and a staffed or monitored lobby turn the building's quietest hours from its weakest into its most controlled.",
      },
    ],
  },
  {
    slug: "northern-remote-site-security-canada",
    title: "Remote Site and Camp Security in Northern Canada",
    excerpt: "Mining camps, remote work sites and northern communities face security challenges most providers never see. What works up north.",
    date: "2026-05-14",
    image: images.patrol,
    sections: [
      {
        h: "Distance changes everything",
        p: "At a remote site, the nearest police response can be hours away and the nearest replacement guard a flight away. Security planning for camps and northern operations has to assume self-reliance: thorough vetting before deployment, clear escalation protocols and equipment that survives the environment.",
      },
      {
        h: "Camp security is community security",
        p: "Remote camps concentrate hundreds of workers in close quarters for weeks at a time. Access control at the gate, enforcement of camp rules, substance policy support and a calm, professional presence prevent the interpersonal incidents that shut down rotations and morale.",
      },
      {
        h: "Choose providers with northern experience",
        p: "Territorial licensing, fly-in logistics, extreme cold and limited connectivity defeat providers who learn on your dime. Ask for specific northern deployment experience, references from remote operations, and a supervision plan that works when the site is a charter flight from anywhere.",
      },
    ],
  },
  {
    slug: "choosing-licensed-security-guard-company-canada",
    title: "How to Choose a Licensed Security Guard Company in Canada",
    excerpt: "Licensing, insurance, training and supervision — the four checks that separate professional security companies from risky ones.",
    date: "2026-05-12",
    image: images.guard,
    sections: [
      {
        h: "Verify provincial licensing first",
        p: "Every Canadian province regulates security guards and the companies that employ them. Before signing anything, confirm the company holds a valid business licence in your province — the PSISA registry in Ontario, the Bureau de la sécurité privée in Quebec, or the Security Programs registry in British Columbia. A legitimate company will give you its licence number without hesitation, and every guard on your site should carry an individual licence card you can ask to see.",
      },
      {
        h: "Ask about training beyond the minimum",
        p: "Provincial basic training is a floor, not a ceiling. Ask how the company trains guards on de-escalation, report writing, first aid and site-specific procedures. Companies that invest in ongoing training produce guards who solve problems instead of creating them.",
      },
      {
        h: "Understand how guards are supervised",
        p: "The difference between a guard who performs and one who sleeps in a stairwell is supervision. Ask how often field supervisors visit sites, how patrol checkpoints are verified, and how you will receive incident reports. GPS-verified patrols and time-stamped digital logs should be standard, not premium add-ons.",
      },
      {
        h: "Get the insurance details in writing",
        p: "A professional security company carries commercial general liability insurance and can name your organization as additionally insured on request. If a company hesitates to share a certificate of insurance, that hesitation is your answer.",
      },
    ],
  },
  {
    slug: "ontario-security-guard-licensing-psisa",
    title: "Ontario Security Guard Licensing: What the PSISA Means for Your Business",
    excerpt: "Ontario's Private Security and Investigative Services Act sets the rules every guard and security business must follow. Here's what clients should know.",
    date: "2026-04-28",
    image: images.commercial,
    sections: [
      {
        h: "The PSISA in plain language",
        p: "The Private Security and Investigative Services Act, 2005 governs every security guard working in Ontario. It requires guards to complete a 40-hour basic training course, pass a ministry exam, and hold a valid licence before working a single shift. Businesses that supply guards must hold their own agency licence.",
      },
      {
        h: "What it means when you hire",
        p: "When you contract a security company in Ontario, you are legally entitled to ask for proof of licensing — both the agency licence and the individual licences of guards assigned to your property. Unlicensed guarding exposes your organization to liability and can complicate insurance claims after an incident.",
      },
      {
        h: "Uniforms, vehicles and identification",
        p: "The PSISA regulates how guards present themselves: uniforms and marked vehicles must not resemble police, and guards must carry their licence on duty. These rules protect the public and give clients a simple way to verify professionalism at a glance.",
      },
    ],
  },
  {
    slug: "construction-site-theft-prevention-canada",
    title: "Construction Site Theft in Canada: A Practical Prevention Playbook",
    excerpt: "Copper, tools and heavy equipment are walking off Canadian job sites at record rates. Here's how layered security stops it.",
    date: "2026-04-10",
    image: images.construction,
    sections: [
      {
        h: "Why job sites are targeted",
        p: "Construction sites combine high-value assets with open perimeters, rotating crews and poor lighting — everything a thief looks for. Industry estimates put construction theft losses in Canada in the hundreds of millions annually, with recovery rates in the single digits.",
      },
      {
        h: "Layer one: deterrence",
        p: "Visible measures do the heavy lifting: fencing, lighting, signage and a uniformed guard presence. A marked patrol vehicle circling a site at unpredictable intervals convinces most opportunists to move on to an easier target.",
      },
      {
        h: "Layer two: detection and response",
        p: "Monitored cameras with two-way audio let operators challenge intruders in real time, while motion-activated alerts bring a patrol response in minutes. Every incident should end with a time-stamped report you can hand to police and your insurer.",
      },
      {
        h: "Layer three: process",
        p: "Tool inventories, equipment GPS tagging, controlled delivery windows and strict sign-out procedures close the internal gaps that external guards can't see. Security works best when site process and physical protection are designed together.",
      },
    ],
  },
  {
    slug: "fire-watch-guards-requirements-canada",
    title: "Fire Watch Requirements: When Your Building Needs Guards on Patrol",
    excerpt: "A failed alarm or sprinkler system doesn't just create risk — it creates a legal obligation. Understanding fire watch keeps buildings open and compliant.",
    date: "2026-03-22",
    image: images.patrol,
    sections: [
      {
        h: "When a fire watch is required",
        p: "Provincial fire codes across Canada require a fire watch whenever a building's fire alarm or sprinkler system is out of service for a significant period — during system repairs, renovations, hot work or after a failure. The authority having jurisdiction (usually the local fire department) can order one immediately.",
      },
      {
        h: "What fire watch guards actually do",
        p: "Fire watch guards patrol all affected areas continuously, watching for smoke, fire and hazards, with the means to alert occupants and call the fire department instantly. Every patrol round is logged with times and observations — that log is your proof of compliance.",
      },
      {
        h: "Documentation protects you",
        p: "If an incident occurs while your system is down, insurers and investigators will ask one question first: was a fire watch in place? Time-stamped logs from professional guards turn a potential liability into a documented, defensible response.",
      },
    ],
  },
  {
    slug: "event-security-planning-checklist",
    title: "Event Security Planning: A Checklist for Canadian Venues and Organizers",
    excerpt: "From crowd flow to emergency egress — the planning steps that keep festivals, conferences and concerts safe.",
    date: "2026-03-05",
    image: images.guard,
    sections: [
      {
        h: "Start with a risk assessment",
        p: "Every event security plan begins with honest questions: How many attendees? Alcohol service? High-profile guests? Protest risk? The answers determine guard ratios, screening levels and emergency procedures. A professional security partner will walk the venue before quoting anything.",
      },
      {
        h: "Screening and access control",
        p: "Ticketing checks, bag searches and magnetometer lanes are your first filter. Design queue lines that keep crowds comfortable and sightlines open — frustrated queues create their own security problems.",
      },
      {
        h: "Emergency planning is non-negotiable",
        p: "Your plan needs evacuation routes, a communications protocol, a command post and a clear chain of decision-making. Guards should be briefed on the venue's specifics, not learning the layout on show day.",
      },
    ],
  },
  {
    slug: "mobile-patrol-vs-static-guards",
    title: "Mobile Patrol vs. Static Guards: Choosing the Right Coverage",
    excerpt: "Both models deter crime — but they solve different problems. How to match coverage to your property and risk profile.",
    date: "2026-02-18",
    image: images.patrol,
    sections: [
      {
        h: "When a static guard is worth it",
        p: "A dedicated on-site guard makes sense when risk is continuous: busy retail floors, active construction sites, hospital emergency departments or any property where incidents unfold in minutes. Static guards also handle access control and customer-facing duties that patrols can't.",
      },
      {
        h: "Where mobile patrol wins",
        p: "For large, low-traffic or multi-site properties, randomized vehicle patrols deliver visible deterrence across a wide area at a fraction of the cost of a full-time post. GPS-verified checkpoints give you proof every visit happened.",
      },
      {
        h: "The hybrid answer",
        p: "Many properties run both: a static guard during high-risk hours and mobile patrols overnight. A proper risk assessment maps your incident history against coverage hours to find the combination that actually matches your exposure.",
      },
    ],
  },
  {
    slug: "retail-loss-prevention-strategies",
    title: "Retail Loss Prevention Strategies That Actually Move the Needle",
    excerpt: "Shrinkage is climbing across Canadian retail. These are the controls — human and procedural — that bring it back down.",
    date: "2026-01-30",
    image: images.surveillance,
    sections: [
      {
        h: "The shrinking margin problem",
        p: "Retail Council of Canada reporting shows shrinkage climbing year over year, driven by organized retail crime, opportunistic theft and internal loss. For most stores, a one-point reduction in shrink flows directly to profit.",
      },
      {
        h: "People beat technology alone",
        p: "Cameras and EAS tags record theft; trained people prevent it. Plain-clothes loss prevention officers identify behaviours before the act, while uniformed greeters at entrances measurably reduce walk-in theft. The most effective programs pair both.",
      },
      {
        h: "Don't ignore internal loss",
        p: "Employee theft and process failures account for a major share of shrink. Refund controls, exception reporting at the point of sale, and receiving-area oversight close gaps that floor guards never see.",
      },
    ],
  },
  {
    slug: "concierge-security-condo-front-desk",
    title: "Concierge Security: Why the Front Desk Is the Heart of Condo Safety",
    excerpt: "The modern condominium concierge blends hospitality with protection — and residents notice the difference immediately.",
    date: "2026-01-15",
    image: images.commercial,
    sections: [
      {
        h: "Two jobs, one post",
        p: "A concierge guard greets residents, manages packages and books amenities — while simultaneously screening visitors, monitoring cameras and controlling keys. It is the only security role where service quality and vigilance carry equal weight.",
      },
      {
        h: "What good coverage looks like",
        p: "Strong condo security programs pair 24/7 concierge coverage with scheduled common-area and parking patrols, documented visitor management, and clear escalation paths for noise complaints, water leaks and emergencies. Boards should expect detailed daily logs, not verbal summaries.",
      },
      {
        h: "The resident experience dividend",
        p: "Buildings with professional concierge programs see it in the numbers: fewer unauthorized entries, faster incident response and measurably higher resident satisfaction. Security becomes an amenity, not a complaint line.",
      },
    ],
  },
  {
    slug: "alarm-response-keyholding-property-managers",
    title: "Alarm Response and Keyholding: What Property Managers Should Know",
    excerpt: "When the alarm goes off at 3 a.m., who attends? Professional keyholding ends the era of managers sleeping next to their phones.",
    date: "2025-12-20",
    image: images.patrol,
    sections: [
      {
        h: "The problem with self-response",
        p: "Many property managers and business owners still attend their own alarms — driving to dark buildings at all hours to face whatever triggered the sensor. Beyond the personal risk, staff attending alone have no backup, no documentation and no deterrence value.",
      },
      {
        h: "How professional keyholding works",
        p: "A security company holds your keys under audited control. When an alarm activates, the nearest patrol unit is dispatched, attends the site, inspects interior and exterior, resets the system if it's a false alarm, and sends you a time-stamped report before morning.",
      },
      {
        h: "What to ask a provider",
        p: "Response-time guarantees, key-control audits, GPS-tracked dispatch and per-incident reporting are the markers of a professional operation. Ask for their average response time in your area — a real company measures it.",
      },
    ],
  },
  {
    slug: "warehouse-security-supply-chain",
    title: "Warehouse Security: Protecting Inventory Across the Supply Chain",
    excerpt: "From gatehouse to loading dock, layered controls keep shrinkage and cargo theft out of your operation.",
    date: "2025-12-02",
    image: images.construction,
    sections: [
      {
        h: "The modern warehouse threat picture",
        p: "Warehouses hold concentrated, portable value — and organized cargo theft crews know it. Losses happen at the gate, at the dock and from within. Effective warehouse security treats all three vectors at once.",
      },
      {
        h: "Control the gate, control the site",
        p: "Every truck, trailer and driver entering your yard should be verified against a manifest, logged and directed. Seal verification on inbound and outbound trailers turns the gatehouse from a formality into your strongest control point.",
      },
      {
        h: "Layered interior controls",
        p: "CCTV coverage of docks and high-value cages, inventory exception reporting, and uniformed guard presence during shipping windows create overlapping protection. The goal is simple: no single failure — human or technical — should allow product to walk away.",
      },
    ],
  },
];

export const insightBySlug = Object.fromEntries(insights.map((i) => [i.slug, i]));
